<script>
    import { onMount } from 'svelte';
    import { page } from '$app/stores';
    import { t } from '$lib/i18n/translations';
    import Omnibox from '$components/save/Omnibox.svelte';
    import Meowbalt from '$components/misc/Meowbalt.svelte';
    import SupportedServices from '$components/save/SupportedServices.svelte';

    // This onMount function runs in the browser only after the page has loaded
    onMount(() => {
        // 1. Get the parameters from the browser's address bar
        const params = $page.url.searchParams;
        const urlToPaste = params.get('url');
        const formatToSelect = params.get('format');

        // 2. If a URL is found in the address bar, proceed
        if (urlToPaste) {
            const inputElement = document.getElementById('link-area');
            const formatButton = document.getElementById(`setting-button-save-downloadMode-${formatToSelect}`);
            const downloadButton = document.getElementById('download-button');

            if (inputElement) {
                // 3. Paste the URL into the input box
                inputElement.value = decodeURIComponent(urlToPaste);
                // Trigger an "input" event so the component recognizes the change
                inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            }

            if (formatButton) {
                // 4. Click the correct format button (auto, audio, or mute)
                formatButton.click();
            }
            
            // 5. Use a short delay to ensure the UI has updated, then click the download button
            if (downloadButton) {
                setTimeout(() => {
                    downloadButton.click();
                }, 100); // 100ms delay
            }
        }
    });
</script>

<svelte:head>
    <title>{$t("DLROX - On Fire")}</title>
    <meta property="og:title" content={$t("DLROX - On Fire")} />
</svelte:head>

<div id="cobalt-save-container" class="center-column-container">
    <div class="supported-services-display">
        <SupportedServices />
    </div>

    <main id="cobalt-save" tabindex="-1" data-first-focus>
        <div class="meowbalt-display">
            <Meowbalt emotion="smile" />
        </div>
        <div class="omnibox-container">
            <Omnibox />
        </div>
    </main>
</div>

<style>
    /* These styles are now more specific to the Cyberpunk theme structure */
    #cobalt-save-container {
        padding: var(--padding);
        overflow: hidden;
    }

    #cobalt-save {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 650px; /* Increased max-width slightly for the new look */
        height: 100%;
        gap: 25px;
    }

    /* Styling for the new wrappers */
    .supported-services-display {
        color: var(--gray);
        font-size: 14px;
        letter-spacing: 1px;
        text-transform: uppercase;
        opacity: 0.7;
    }
    
    [data-theme*="punk"] .supported-services-display,
    [data-theme*="mirage"] .supported-services-display,
    [data-theme*="toxic"] .supported-services-display {
        text-shadow: var(--cyber-glow-secondary);
        opacity: 1;
    }

    .meowbalt-display {
        --clip-size: 20px;
        clip-path: polygon(
            var(--clip-size) 0%, 
            100% 0, 
            100% calc(100% - var(--clip-size)), 
            calc(100% - var(--clip-size)) 100%, 
            0 100%, 
            0 var(--clip-size)
        );
        padding: 5px;
        transition: all 0.3s ease;
    }

    [data-theme*="punk"] .meowbalt-display,
    [data-theme*="mirage"] .meowbalt-display,
    [data-theme*="toxic"] .meowbalt-display {
        border: 2px solid var(--button-stroke);
        box-shadow: var(--cyber-glow);
    }
    
    .omnibox-container {
        position: relative;
        width: 100%;
    }

    /* Style the Omnibox component within this container */
    .omnibox-container :global(.omnibox) {
        --clip-size: 15px;
        border-radius: 0;
        background-color: transparent;
        transition: all 0.3s ease;
        padding: 15px 20px;
    }

    [data-theme*="punk"] .omnibox-container :global(.omnibox),
    [data-theme*="mirage"] .omnibox-container :global(.omnibox),
    [data-theme*="toxic"] .omnibox-container :global(.omnibox) {
        background-color: rgba(0,0,0,0.2);
        backdrop-filter: blur(5px);
        border: 1px solid var(--input-border);
        clip-path: polygon(
            var(--clip-size) 0%, 
            100% 0, 
            100% calc(100% - var(--clip-size)), 
            calc(100% - var(--clip-size)) 100%, 
            0 100%, 
            0 var(--clip-size)
        );
    }
    
    .omnibox-container:focus-within {
        box-shadow: var(--cyber-glow);
    }

</style>
